import { useMemo } from 'react'

import { sortArrayBy } from '@domain-group/javascript-utils'
import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'
import type { UseLoadMorePayload } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { ModuleState } from '@redux/modules/types'

const watchEntityKeys = ['geoCountries']

type GeoCountriesFilters = {
  active?: 'true' | 'false',
}

interface Options {
  deps?: any[],
  entityKey?: string,
  filters?: GeoCountriesFilters,
  performHttpRequests?: boolean,
}

function useGeoCountries(options: Options = {}) {
  const { deps = [], entityKey, filters = {} } = options
  const filterActive = filters?.active

  const {
    updatedEntities: { geoCountries: geoCountriesUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const geoCountries = useSelector(reduxState => reduxState.entities.geoCountries)
  const defaultGeoCountry = Object.values(geoCountries).find(country => country.code === 'AU')

  const filteredGeoCountries = useMemo(() => {
    const filtered = Object.values(geoCountries).filter((geoCountry) => {
      const { active } = geoCountry

      const matchActive = filterActive ? filterActive === active?.toString() : true
      return matchActive
    })

    return sortArrayBy(filtered, 'asc', 'title')
  }, [geoCountriesUpdatedAt, JSON.stringify(filters)])

  const filteredGeoCountriesCount = filteredGeoCountries.length
  const hasFilteredGeoCountries = !!filteredGeoCountriesCount

  const loadMorePayload: UseLoadMorePayload = useLoadMore({
    loadedCount: filteredGeoCountriesCount,
    filters,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingGeoCountries } = useReduxAction(
    'geoCountries',
    'loadGeoCountries',
    {
      entityKey,
      ...filtersWithOffset,
      limit,
    },
    [filters, performHttpRequests, ...deps],
    {
      shouldPerformFn: ({ loading }: ModuleState) => !!performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    defaultGeoCountry,
    filteredGeoCountries,
    filteredGeoCountriesCount,
    hasFilteredGeoCountries,
    loading: loadingGeoCountries,
  }
}

export default useGeoCountries
